import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Match, NewMatch, Job, Educator } from '@/types/admin'
import { JobsService } from '@/services/jobs'
import { EducatorsService } from '@/services/educators'

const matchSchema = z.object({
  job_id: z.string().min(1, 'Vaga é obrigatória'),
  educator_id: z.string().min(1, 'Educador é obrigatório'),
  status: z.enum(['sent', 'interview', 'hired', 'rejected']),
  internal_notes: z.string().optional(),
})

interface MatchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  match?: Match | null
  onSubmit: (data: NewMatch) => Promise<void>
}

export function MatchDialog({
  open,
  onOpenChange,
  match,
  onSubmit,
}: MatchDialogProps) {
  const [jobs, setJobs] = useState<Job[]>([])
  const [educators, setEducators] = useState<Educator[]>([])
  const [loadingData, setLoadingData] = useState(false)

  const form = useForm<z.infer<typeof matchSchema>>({
    resolver: zodResolver(matchSchema),
    defaultValues: {
      job_id: '',
      educator_id: '',
      status: 'sent',
      internal_notes: '',
    },
  })

  useEffect(() => {
    if (open) {
      setLoadingData(true)
      Promise.all([JobsService.getAll(), EducatorsService.getAll()])
        .then(([jobsData, educatorsData]) => {
          setJobs(jobsData)
          setEducators(educatorsData)
        })
        .finally(() => setLoadingData(false))
    }
  }, [open])

  useEffect(() => {
    if (match) {
      form.reset({
        job_id: match.job_id,
        educator_id: match.educator_id,
        status: match.status,
        internal_notes: match.internal_notes || '',
      })
    } else {
      form.reset({
        job_id: '',
        educator_id: '',
        status: 'sent',
        internal_notes: '',
      })
    }
  }, [match, form, open])

  const handleSubmit = async (data: z.infer<typeof matchSchema>) => {
    await onSubmit(data)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {match ? 'Editar Match' : 'Novo Match Manual'}
          </DialogTitle>
        </DialogHeader>

        {loadingData ? (
          <div className="py-10 text-center text-slate-500">
            Carregando listas...
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="job_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vaga / Escola</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={!!match}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a vaga" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {jobs.map((j) => (
                            <SelectItem key={j.id} value={j.id}>
                              {j.school_name} - {j.position} ({j.city})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="educator_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Educador</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={!!match}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o educador" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {educators.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {e.full_name} - {e.desired_position} ({e.city})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4 pt-2 border-t">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status do Processo</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="sent">
                            Enviado / Sugerido
                          </SelectItem>
                          <SelectItem value="interview">
                            Em Entrevista
                          </SelectItem>
                          <SelectItem value="hired">Contratado</SelectItem>
                          <SelectItem value="rejected">Não Avançou</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="internal_notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notas Internas</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Registre o andamento, feedbacks ou observações..."
                          className="min-h-[100px]"
                        />
                      </FormControl>
                      <FormDescription>
                        Essas anotações são visíveis apenas para a equipe
                        administrativa.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-brand-emerald text-white">
                  Salvar Match
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
